import React, { useState, useMemo } from "react";
import axios from "axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { formatBaseLabel } from "../../../utils/baseDisplay";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";

const InventoryTable = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [inventoryData, setInventoryData] = useState([]);

  // baseFilter: "ALL" | "A" | "B"
  const [baseFilter, setBaseFilter] = useState("ALL");

  const [dia, setDia] = useState(() => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    // HTML date = YYYY-MM-DD
    return `${yyyy}-${mm}-${dd}`;
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const getStatusColor = () => "text-muted-foreground bg-muted";
  const getStatusLabel = () => "Normal";

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev?.includes(itemId)
        ? prev?.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems?.length === filteredData?.length
        ? []
        : filteredData?.map((item) => item?.id)
    );
  };

  const handleEdit = (item) => {
    setEditingItem(item?.id);
    setEditValues({
      minStock: item?.minStock ?? 0,
      maxStock: item?.maxStock ?? 0,
    });
  };

  const handleSaveEdit = () => {
    console.log("Salvar edição (mock):", editingItem, editValues);
    setEditingItem(null);
    setEditValues({});
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditValues({});
  };

  /**
   * Buscar estoque (consolidado ou por base) no backend
   */
  const handleBuscarEstoque = async () => {
    setLoading(true);
    setErrorMsg("");
    setSelectedItems([]);

    try {
      const response = await axios.get(
        `${API_BASE_URL.replace(/\/+$/, "")}/api/estoque`,
        {
          params: {
            dia, // formato YYYY-MM-DD
            tags: searchTerm || undefined,
            base: baseFilter || "ALL",
          },
        }
      );

      const data = response.data;

      if (!data || data.ok === false) {
        throw new Error(
          data?.message ||
            "Resposta inválida do servidor ao consultar estoque."
        );
      }

      const itens = Array.isArray(data.itens) ? data.itens : [];

      // Normalizar: criar ID único e campos de código separados por base
      const normalizados = itens.map((item) => {
        const codigosA = Array.isArray(item.codigosBaseA)
          ? item.codigosBaseA
          : item.codigoBaseA != null
          ? [item.codigoBaseA]
          : [];
        const codigosB = Array.isArray(item.codigosBaseB)
          ? item.codigosBaseB
          : item.codigoBaseB != null
          ? [item.codigoBaseB]
          : [];

        const codigoA = codigosA.join(", ");
        const codigoB = codigosB.join(", ");

        const id =
          `${item.produto || ""}::A:${codigoA || "-"}::B:${codigoB || "-"}`;

        return {
          ...item,
          id,
          codigoA,
          codigoB,
        };
      });

      setInventoryData(normalizados);
    } catch (err) {
      let texto = "";

      if (err.response && typeof err.response.data === "string") {
        // servidor respondeu HTML (ex: 404)
        texto = `Resposta não-JSON do servidor: ${err.response.data}`;
      } else if (err.response && typeof err.response.data === "object") {
        texto =
          err.response.data?.message ||
          JSON.stringify(err.response.data, null, 2);
      } else if (err.message) {
        texto = err.message;
      } else {
        texto = String(err);
      }

      setErrorMsg(texto);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let filtered = inventoryData?.filter((item) => {
      const termo = searchTerm?.toLowerCase() || "";
      return (
        item?.produto?.toLowerCase()?.includes(termo) ||
        String(item?.codigoA || "")
          .toLowerCase()
          .includes(termo) ||
        String(item?.codigoB || "")
          .toLowerCase()
          .includes(termo)
      );
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === "asc" ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [inventoryData, searchTerm, sortConfig]);

  const renderSparkline = () => null;

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 text-left font-body-medium text-xs text-muted-foreground hover:text-card-foreground transition-smooth"
    >
      <span>{children}</span>
      <Icon
        name={
          sortConfig?.key === column
            ? sortConfig?.direction === "asc"
              ? "ChevronUp"
              : "ChevronDown"
            : "ChevronsUpDown"
        }
        size={14}
      />
    </button>
  );

  const renderCodigos = (item) => {
    const a = item.codigoA || "-";
    const b = item.codigoB || "-";

    if (baseFilter === "A") return `${formatBaseLabel("A")}: ${a}`;
    if (baseFilter === "B") return `${formatBaseLabel("B")}: ${b}`;
    return `${formatBaseLabel("A")}: ${a} | ${formatBaseLabel("B")}: ${b}`;
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4 gap-4">
          <h3 className="font-heading-semibold text-lg text-card-foreground">
            Inventário Detalhado — Estoque por Dia
          </h3>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dia}
              onChange={(e) => setDia(e.target.value)}
              className="w-44"
            />

            {/* DROPDOWN de base */}
            <select
              value={baseFilter}
              onChange={(e) => setBaseFilter(e.target.value)}
              className="h-9 px-2 rounded-md border border-border bg-background text-sm font-body-medium text-card-foreground"
            >
              <option value="ALL">{formatBaseLabel("ALL")}</option>
              <option value="A">Somente {formatBaseLabel("A")}</option>
              <option value="B">Somente {formatBaseLabel("B")}</option>
            </select>

            <Button
              variant="primary"
              size="sm"
              onClick={handleBuscarEstoque}
              disabled={loading}
            >
              {loading ? "Buscando..." : "Buscar"}
            </Button>

            <Input
              type="search"
              placeholder="Buscar produtos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-64"
            />
          </div>
        </div>

        {errorMsg && (
          <div className="text-xs text-error bg-error/10 rounded px-3 py-2">
            {errorMsg}
          </div>
        )}

        {selectedItems?.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-accent rounded-lg mt-4">
            <span className="text-sm font-body-medium text-accent-foreground">
              {selectedItems?.length} item(s) selecionado(s)
            </span>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" iconName="Edit">
                Editar em Lote
              </Button>
              <Button variant="outline" size="sm" iconName="ShoppingCart">
                Criar Pedido
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left w-8">
                <input
                  type="checkbox"
                  checked={
                    selectedItems?.length === filteredData?.length &&
                    filteredData?.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="p-3 text-left">
                <SortButton column="produto">Produto</SortButton>
              </th>
              <th className="p-3 text-left">
                <SortButton column="estoque">Estoque</SortButton>
              </th>
              <th className="p-3 text-left">
                <SortButton column="custo">Custo Unit.</SortButton>
              </th>
              <th className="p-3 text-left">
                <SortButton column="total">Total</SortButton>
              </th>
              <th className="p-3 text-left">Tendência</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((item) => (
              <tr
                key={item.id}
                className="border-b border-border hover:bg-accent/50 transition-smooth"
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedItems?.includes(item.id)}
                    onChange={() => handleSelectItem(item.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div>
                    <div className="font-body-medium text-sm text-card-foreground">
                      {item.produto}
                    </div>
                    <div className="text-xs text-muted-foreground font-caption">
                      {renderCodigos(item)}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm font-body-medium text-card-foreground font-data">
                    {item.estoque}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-sm font-body-medium text-card-foreground font-data">
                    R$ {Number(item.custo || 0).toFixed(2)}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-sm font-body-medium text-card-foreground font-data">
                    R$ {Number(item.total || 0).toFixed(2)}
                  </span>
                </td>
                <td className="p-3">{renderSparkline()}</td>
                <td className="p-3">
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-caption ${getStatusColor(
                      item.status
                    )}`}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex space-x-1">
                    {editingItem === item.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-success hover:bg-success/10 rounded transition-smooth"
                          title="Salvar"
                        >
                          <Icon name="Check" size={14} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-error hover:bg-error/10 rounded transition-smooth"
                          title="Cancelar"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1 text-muted-foreground hover:text-card-foreground hover:bg-accent rounded transition-smooth"
                          title="Editar"
                        >
                          <Icon name="Edit" size={14} />
                        </button>
                        <button
                          className="p-1 text-muted-foreground hover:text-card-foreground hover:bg-accent rounded transition-smooth"
                          title="Ver detalhes"
                        >
                          <Icon name="Eye" size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="p-6 text-center text-sm text-muted-foreground"
                >
                  Nenhum item para exibir.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p-4 border-t border-border flex items-center justify-between text-sm text-muted-foreground">
        <span>Mostrando {filteredData?.length} itens</span>
      </div>
    </div>
  );
};

export default InventoryTable;
